import React, { useState, useCallback } from 'react';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button, 
  Box, 
  Typography, 
  Slider, 
  CircularProgress,
  Alert
} from '@mui/material';
import { useDropzone } from 'react-dropzone';
import Cropper from 'react-easy-crop';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from 'react-i18next';

// Fonction pour créer une image recadrée à partir du canvas
const createImage = (url) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (error) => reject(error));
    image.src = url;
  });

// Fonction pour obtenir un canvas recadré
// Removed useTranslation hook from this utility function
const getCroppedImg = async (imageSrc, pixelCrop) => {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  
  // Définir les dimensions du canvas
  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;

  // Dessiner l'image recadrée sur le canvas
  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    pixelCrop.width,
    pixelCrop.height
  );

  // Convertir le canvas en blob
  return new Promise((resolve) => {
    canvas.toBlob((blob) => {
      resolve(blob);
    }, 'image/jpeg', 0.95);
  });
};

const ProfileImageUpload = ({ open, onClose }) => {
  const { updateProfilePicture } = useAuth();
  const { t } = useTranslation(); // Only use hook inside the component
  
  const [image, setImage] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  
  // Fonction pour gérer le dépôt de fichier
  const onDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles && acceptedFiles.length > 0) {
      const file = acceptedFiles[0];
      
      // Vérifier le type de fichier
      if (!file.type.startsWith('image/')) {
        setError(t('profile.photo.invalidType', 'Veuillez sélectionner une image valide.'));
        return;
      }
      
      // Vérifier la taille du fichier (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError(t('profile.photo.sizeLimit', 'L\'image ne doit pas dépasser 5MB.'));
        return;
      }
      
      setError('');
      const reader = new FileReader();
      reader.onload = () => {
        setImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  }, [t]);
  
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif']
    },
    maxFiles: 1
  });
  
  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);
  
  const handleSave = async () => {
    try {
      if (!image) {
        setError(t('profile.photo.noImageSelected', 'Veuillez sélectionner une image.'));
        return;
      }
      
      setLoading(true);
      setError('');
      setSuccess(false);
      
      // Recadrer l'image
      const croppedImage = await getCroppedImg(image, croppedAreaPixels);
      
      console.log('Cropped image blob:', croppedImage);
      // Créer un objet FormData pour l'envoi
      const formData = new FormData();
      formData.append('profilePicture', croppedImage, 'profile.jpg');

      // Log pour vérifier le contenu du FormData
      for (let pair of formData.entries()) {
          console.log(pair[0] + ': ' + pair[1]);
      }
      
      // Appeler la fonction de mise à jour de la photo de profil
      const result = await updateProfilePicture(formData);
      
      if (result.success) {
        setSuccess(true);
        // Fermer la boîte de dialogue après un court délai
        setTimeout(() => {
          onClose();
          window.location.reload();
        }, 1500);
      } else {
        setError(result.message || t('profile.photo.genericError', 'Une erreur est survenue lors du téléchargement de l\'image.'));
      }
    } catch (err) {
      console.error('Erreur lors du téléchargement de l\'image:', err);
      setError(t('profile.photo.tryAgain', 'Une erreur est survenue. Veuillez réessayer.'));
    } finally {
      setLoading(false);
    }
  };
  
  const handleCancel = () => {
    setImage(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedAreaPixels(null);
    setError('');
    setSuccess(false);
    onClose();
  };
  
  return (
    <Dialog 
      open={open} 
      onClose={loading ? undefined : handleCancel}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>{t('profile.photo.upload')}</DialogTitle>
      
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
           {t('profile.photo.updateSucces')}
          </Alert>
        )}
        
        {!image ? (
          <Box 
            {...getRootProps()} 
            sx={{ 
              border: '2px dashed #cccccc', 
              borderRadius: 2, 
              p: 4, 
              textAlign: 'center',
              cursor: 'pointer',
              bgcolor: isDragActive ? 'rgba(0, 0, 0, 0.05)' : 'transparent',
              '&:hover': {
                bgcolor: 'rgba(0, 0, 0, 0.05)'
              }
            }}
          >
            <input {...getInputProps()} />
            <Typography variant="body1" gutterBottom>
              {isDragActive
                ? t('profile.photo.dropHere', 'Déposez l\'image ici...')
                : t('profile.photo.dragOrClick', 'Glissez et déposez une image ici, ou cliquez pour sélectionner un fichier')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('profile.photo.format')}
            </Typography>
          </Box>
        ) : (
          <>
            <Box sx={{ position: 'relative', height: 300, mb: 2 }}>
              <Cropper
                image={image}
                crop={crop}
                zoom={zoom}
                aspect={1}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
              />
            </Box>
            
            <Box sx={{ px: 2 }}>
              <Typography gutterBottom>{t('profile.photo.zoom', 'Zoom')}</Typography>
              <Slider
                value={zoom}
                min={1}
                max={3}
                step={0.1}
                aria-labelledby="zoom-slider"
                onChange={(e, newValue) => setZoom(newValue)}
              />
            </Box>
          </>
        )}
      </DialogContent>
      
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button 
          onClick={handleCancel} 
          disabled={loading}
        >
          {t('common.cancel', 'Annuler')}
        </Button>
        <Button 
          onClick={handleSave} 
          variant="contained" 
          color="primary" 
          disabled={!image || loading}
          startIcon={loading && <CircularProgress size={20} color="inherit" />}
        >
          {loading ? t('profile.photo.uploading', 'Téléchargement...') : t('common.save', 'Enregistrer')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProfileImageUpload;